import { FormControl, FormLabel, Input, List, ListItem, Text, Box, Button, Flex } from '@chakra-ui/react';
import { useFormContext, Controller } from 'react-hook-form';
import { FieldProps } from 'shared/types/filters';
import useIsInvalid from './hooks/useIsInvalid';
import ErrorMessage from './ErrorMessage';
import MuiIcon, { IconName } from 'components/MuiIcon';
import CutText from '../CutText';

enum FileTypeE {
  IMAGE = 'image',
  VIDEO = 'video',
  DOC = 'application',
  AUDIO = 'audio'
}

const FileField = ({
  label,
  required,
  acceptedFileTypes,
  name,
  ...props
}: FieldProps<'file', 'input'>) => {
  const isInvalid = useIsInvalid(name);
  const { control, watch, setValue } = useFormContext();
  const files: Array<File> = Array.from(watch(name) ?? []);

  const renderFileIcon = (mimeType: File['type']): IconName => {
    let fileType = FileTypeE.DOC;
    if (mimeType.includes(FileTypeE.IMAGE)) fileType = FileTypeE.IMAGE;
    if (mimeType.includes(FileTypeE.VIDEO)) fileType = FileTypeE.VIDEO;
    if (mimeType.includes(FileTypeE.AUDIO)) fileType = FileTypeE.AUDIO;
    return {
      [FileTypeE.IMAGE]: 'image' as const,
      [FileTypeE.VIDEO]: 'video_file' as const,
      [FileTypeE.DOC]: 'description' as const,
      [FileTypeE.AUDIO]: 'audio_file' as const,
    }[fileType]
  }

  const handleDelete = (file: File) => {
    const filteredFiles = files.filter((item) => item.name !== file.name);
    setValue(name, filteredFiles)
  }

  return (
    <FormControl {...props} isInvalid={isInvalid} isRequired={required}>
      <FormLabel fontSize="md" fontWeight="500" mb="8px">{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        defaultValue={null}
        render={({ field }) => (
          <Input
            type="file"
            multiple
            accept={acceptedFileTypes}
            onChange={(e) => field.onChange([...e.target.files, ...files])}
          />
        )}
      />
      <ErrorMessage name={name} />

      {!!files.length && (
        <Box mt={2}>
          <Text fontWeight="bold" mb={1}>Selected files:</Text>
          <List spacing={1}>
            {Array.from<File>(files).map((file: File, i: number) => (
              <ListItem key={i}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Flex alignItems="center">
                    <MuiIcon name={renderFileIcon(file.type)} />
                    <CutText ml="2" w="250px">{file.name}</CutText>
                  </Flex>
                  <Button onClick={() => handleDelete(file)}>
                    <MuiIcon name="delete" />
                  </Button>
                </Flex>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </FormControl>
  );
};

export default FileField;